import * as React from "react";
import { useGetIdentity } from "@refinedev/core";
import { dataProvider } from "@/providers/dataProvider";
import { canAccess } from "@/providers/accessControlProvider";
import type { AdminIdentity, AdminRole } from "@/providers/types";
import { RoleBadge } from "@/components/RoleBadge";
import { RoleSelector } from "@/components/RoleSelector";
import { AddOperatorForm } from "@/components/AddOperatorForm";
import { SkeletonTable } from "@/components/SkeletonTable";
import { ErrorBanner } from "@/components/ErrorBanner";
import { InlineError } from "@/components/InlineError";
import { ConfirmModal } from "@/components/ConfirmModal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Operator = {
  id: string;
  email: string;
  role: AdminRole;
  assignedBy: string | null;
  createdAt: string;
};

// ---------------------------------------------------------------------------
// Error code mapping
// ---------------------------------------------------------------------------

function mapErrorCode(code: string | undefined): string | null {
  if (code === "admin.last_superadmin_protected") {
    return "Нельзя изменить последнего суперадмина: удаление и смена роли запрещены.";
  }
  if (code === "admin.self_role_change") {
    return "Нельзя изменить собственную роль.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// OperatorRow
// ---------------------------------------------------------------------------

interface OperatorRowProps {
  operator: Operator;
  onRefresh: () => void;
}

function OperatorRow({ operator, onRefresh }: OperatorRowProps): React.JSX.Element {
  const [isEditing, setIsEditing] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState<AdminRole>(operator.role);
  const [inlineError, setInlineError] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  React.useEffect(() => {
    if (!isEditing) {
      setSelectedRole(operator.role);
    }
  }, [operator.role, isEditing]);

  async function handleRoleSave(): Promise<void> {
    setIsSaving(true);
    setInlineError(null);
    try {
      await dataProvider.update({
        resource: "operators",
        id: operator.id,
        variables: { role: selectedRole },
      });
      setIsEditing(false);
      onRefresh();
    } catch (err: unknown) {
      const bffErr = err as { code?: string; message?: string } | null;
      const mapped = mapErrorCode(bffErr?.code);
      setInlineError(mapped ?? bffErr?.message ?? "Ошибка изменения роли");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteConfirmed(): Promise<void> {
    await dataProvider.deleteOne({ resource: "operators", id: operator.id });
    onRefresh();
  }

  function handleCancelEdit(): void {
    setIsEditing(false);
    setSelectedRole(operator.role);
    setInlineError(null);
  }

  const formattedDate = new Date(operator.createdAt).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <tr className="border-t border-[var(--border)] hover:bg-[var(--accent-weak)] transition-colors">
      <td className="px-3.5 py-2.5 text-[13px] font-[500] text-[var(--fg)]">
        {operator.email}
      </td>
      <td className="px-3.5 py-2.5 w-[200px]">
        {isEditing ? (
          <div className="min-w-[160px]">
            <RoleSelector
              value={selectedRole}
              onChange={setSelectedRole}
              disabled={isSaving}
            />
            {inlineError && <InlineError message={inlineError} />}
          </div>
        ) : (
          <>
            <RoleBadge role={operator.role} />
            {inlineError && <InlineError message={inlineError} />}
          </>
        )}
      </td>
      <td className="px-3.5 py-2.5 text-[13px] text-[var(--muted)]">
        {operator.assignedBy ?? "—"}
      </td>
      <td className="px-3.5 py-2.5 text-[12px] text-[var(--muted)] whitespace-nowrap">
        {formattedDate}
      </td>
      <td className="px-3.5 py-2.5 w-[220px]">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={() => { void handleRoleSave(); }}
                disabled={isSaving}
                className="px-3 py-1.5 bg-[var(--accent)] text-white text-[12px] font-[600] rounded-[var(--radius-sm)] hover:bg-[#155bd4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px]"
              >
                {isSaving ? "..." : "Сохранить"}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="px-3 py-1.5 bg-[var(--surface)] text-[var(--fg)] text-[12px] font-[600] border border-[var(--border)] rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)] disabled:opacity-50 transition-colors min-h-[44px] min-w-[44px]"
              >
                Отмена
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-3 py-1.5 bg-[var(--surface)] text-[var(--fg)] text-[12px] font-[600] border border-[var(--border)] rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)] transition-colors min-h-[44px] min-w-[44px]"
              >
                Изменить роль
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3 py-1.5 bg-[rgba(196,58,42,0.06)] text-[var(--danger)] text-[12px] font-[600] border border-[rgba(196,58,42,0.18)] rounded-[var(--radius-sm)] hover:bg-[rgba(196,58,42,0.12)] transition-colors min-h-[44px] min-w-[44px]"
              >
                Удалить
              </button>
            </>
          )}
        </div>
      </td>
      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onSubmit={handleDeleteConfirmed}
        title="Удалить оператора"
        confirmTitle="Подтвердите удаление"
        confirmDescription={`Оператор ${operator.email} будет удалён. Действие зафиксируется в Audit Log.`}
        submitLabel="Удалить"
        submitVariant="destructive"
        doubleConfirm
        requireReason={false}
        footerNote="Действие зафиксируется в Audit Log"
      />
    </tr>
  );
}

// ---------------------------------------------------------------------------
// OperatorsList (page)
// ---------------------------------------------------------------------------

export function OperatorsList(): React.JSX.Element {
  const [operators, setOperators] = React.useState<Operator[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const { data: identity } = useGetIdentity<AdminIdentity>();

  const hasAccess = canAccess({
    resource: "operators",
    action: "list",
    role: identity?.role,
  }).can;

  const fetchOperators = React.useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await dataProvider.getList({ resource: "operators" });
      setOperators(result.data as unknown as Operator[]);
    } catch (err: unknown) {
      const bffErr = err as { message?: string } | null;
      setLoadError(bffErr?.message ?? "Ошибка загрузки операторов");
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (hasAccess) {
      void fetchOperators();
    }
  }, [hasAccess, fetchOperators]);

  if (!hasAccess) {
    return (
      <div className="p-6">
        <p className="text-[var(--muted)] text-[13px]">
          Раздел доступен только для superadmin
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-6 py-5 flex items-baseline gap-3 flex-wrap border-b border-[var(--border)]">
        <h1 className="text-[22px] font-[700] tracking-[-0.3px]">Операторы</h1>
        <span className="text-[13px] text-[var(--muted)]">Управление доступом</span>
        <span className="inline-flex items-center text-[10px] font-[700] uppercase tracking-[0.5px] px-2 py-0.5 rounded-[4px] bg-[rgba(196,138,26,0.12)] text-[#7a5200] ml-1">
          только superadmin
        </span>
      </div>

      <AddOperatorForm onSuccess={() => { void fetchOperators(); }} />

      <div className="px-6 pb-6 flex-1">
        {isLoading && <SkeletonTable columns={5} rows={4} />}

        {!isLoading && loadError && (
          <ErrorBanner
            message={loadError}
            onRetry={() => { void fetchOperators(); }}
          />
        )}

        {!isLoading && !loadError && (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden">
            <table className="w-full border-collapse">
              <thead className="bg-[var(--surface-2)]">
                <tr>
                  <th
                    scope="col"
                    className="px-3.5 py-2.5 text-left text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px] border-b border-[var(--border)] whitespace-nowrap"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-3.5 py-2.5 text-left text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px] border-b border-[var(--border)] whitespace-nowrap w-[200px]"
                  >
                    Роль
                  </th>
                  <th
                    scope="col"
                    className="px-3.5 py-2.5 text-left text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px] border-b border-[var(--border)] whitespace-nowrap w-[160px]"
                  >
                    Назначил
                  </th>
                  <th
                    scope="col"
                    className="px-3.5 py-2.5 text-left text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px] border-b border-[var(--border)] whitespace-nowrap w-[140px]"
                  >
                    Дата назначения
                  </th>
                  <th
                    scope="col"
                    className="px-3.5 py-2.5 text-left text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px] border-b border-[var(--border)] whitespace-nowrap w-[220px]"
                  >
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody>
                {operators.map((op) => (
                  <OperatorRow
                    key={op.id}
                    operator={op}
                    onRefresh={() => { void fetchOperators(); }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
